/* eslint-disable no-console */
import { observable, action, toJS, makeObservable } from 'mobx';
import { navigate } from '@reach/router';
import { client as apolloClient } from '../util/apolloClient';
import { client as api2Client } from '../util/api2Client';
import {
  getCauzesQuery,
  createPlannedEventMutation,
  updatePlannedEventMutation,
  updateCauzeMutation,
} from '../graphql/cauze';
import { isEmpty } from 'lodash';
import {
  createMatchMutation,
  editMatchMutation,
  updateCauzeMatchMutation,
} from '../graphql/match';
import { eventQuery, eventQueryApi2 } from '../graphql/event';

import moment from 'moment';
import axios from 'axios';
import uiStore from './UiStore';

import config from '../config';
import { getToken } from '../util/storage';
import profileStore from '../stores/ProfileStore';
import { createVideoMutation } from 'graphql/content';
import mixpanel from 'mixpanel-browser';

const UPLOAD_URL = `${config.UPLOAD_ROOT}/event/image`;
const UPLOAD_URLV2 = `${config.API_ROOTV2}/api`;
const CAUZE_IMAGE_FORM_KEY = `image`;
const DESCRIPTION_DEFAULT =
  'Let’s make an impact together! Consider donating with me to show your support, even if it’s just a dollar. Collectively we can make a difference.';
const EVENT_DEFAULT_DAY_RANGE = 14;

class CauzeStore {
  @observable cauzes = [];
  @observable cauzesJoinTotal = 0;
  @observable cauzesAmountTotal = 0;
  @observable cauzesLoading = true;
  @observable isError = false;
  @observable isUploadingCauzeImage = false;
  @observable imageUploadError = false;
  @observable menuActiveForCharityId = null;
  @observable matchHasChanged = false;
  @observable cauzeTotals = new Map();
  @observable videoData = null;

  @action getInitial = async ({ userContext }) => {
    this.cauzesLoading = true;
    const options = {
      variables: { userContext },
      query: getCauzesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const res = await apolloClient.query(options);

      this.cauzes = res.data.eventsForContext
        .slice()
        .sort(
          (a, b) => moment(b.insertedAt).unix() - moment(a.insertedAt).unix(),
        );

      this.cauzesAmountTotal = res.data.eventsForContext.reduce(
        (acc, { current }) => acc + current,
        0,
      );
      this.cauzesJoinTotal = res.data.eventsForContext.reduce(
        (acc, { purchases }) => acc + purchases.length,
        0,
      );

      this.cauzesLoading = false;
    } catch (err) {
      this.cauzesLoading = false;
      this.isError = true;
      this.error = err;
      if (global.IS_LOCAL_OR_DEV) {
        console.error(err);
      }
    }
  };

  @action fetchTotals = async ({ userContext, ignoreCache = false }) => {
    const key = JSON.stringify(userContext);

    if (!ignoreCache && this.cauzeTotals.get(key)) {
      return this.cauzeTotals.get(key);
    }

    const options = {
      variables: { userContext },
      query: getCauzesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const res = await apolloClient.query(options);

      const totals = {
        totalCauzes: res.data.eventsForContext.length,
        amountTotal: res.data.eventsForContext.reduce(
          (acc, { current }) => acc + current,
          0,
        ),
        joinTotal: res.data.eventsForContext.reduce(
          (acc, { purchases }) => acc + purchases.length,
          0,
        ),
      };

      this.cauzeTotals.set(key, totals);

      return totals;
    } catch (err) {
      this.cauzesLoading = false;
      this.isError = true;
      this.error = err;
      if (global.IS_LOCAL_OR_DEV) {
        console.error(err);
      }
    }

    return {};
  };

  getCauzeUrl = (userContext) => {
    if (userContext.companyId) {
      return `/admin/company/${userContext.companyId}/cauzes`;
    } else if (userContext.charityId) {
      return `/admin/charity/${userContext.charityId}/cauzes`;
    } else if (userContext.influencerId) {
      return `/admin/influencer/${userContext.influencerId}/cauzes`;
    } else if (userContext.userId) {
      return `/admin/influencer/${userContext.userId}/cauzes`;
    } else {
      return `/admin/cauzes`;
    }
  };

  @action navToCreateCauze = (userContext) => {
    const url = this.getCauzeUrl(userContext);
    navigate(`${url}/create`);
  };

  @action navToEditCauze = (userContext, cauzeId) => {
    const url = this.getCauzeUrl(userContext);
    navigate(`${url}/${cauzeId}/edit`);
  };

  @action navToCauzes = () => {
    navigate('/profile/cauzes');
  };

  @action updatePlannedEventState = async ({ eventId, eventState, index }) => {
    try {
      if (this.cauzes[index]) {
        this.cauzes[index].eventState = eventState;
      }
      await apolloClient.mutate({
        variables: {
          eventId,
          eventState,
        },
        mutation: updatePlannedEventMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });
    } catch (err) {
      this.error = true;
      mixpanel.track('updatePlannedEventState Error', { err: `${err}` });
      err.map((err) =>
        uiStore.showNotification({
          body: err.message,
          type: 'ERROR',
        }),
      );
    }

    this.savingCauze = false;
  };

  // Create a Cauze

  @observable loadingCauze = false;
  @observable loadingCauzeError = false;
  @observable savingCauze = false;
  @observable createCauzeError = false;
  @observable currentCauze = {
    name: '',
    charities: [],
    eventState: 'UNPUBLISHED',
    description: '',
    startDate: '',
    endDate: '',
    imageId: '',
    previewImageUrl: '',
    hasDates: false,
    userMatchLimit: 5000,
    matchTotal: 0,
    hasMatch: false,
    matchType: 'EVENT',
    matchMultiplier: 1,
  };

  @action updateCauze = (props) => {
    this.currentCauze = { ...this.currentCauze, ...props };
    global.IS_LOCAL_OR_DEV && console.log(toJS(this.currentCauze));
  };

  @action setDateDefaults = (hasDates) => {
    this.updateCauze({
      hasDates: hasDates,
      startDate: hasDates ? moment().utc() : '',
      endDate: hasDates ? moment().add(EVENT_DEFAULT_DAY_RANGE, 'd') : '',
    });
  };

  @action toggleHasDates = () => {
    this.setDateDefaults(!this.currentCauze.hasDates);
  };

  @action onToggleMenu = (charityId) => {
    this.menuActiveForCharityId = charityId;
  };

  @action uploadImage = async (imageFile) => {
    if (imageFile) {
      try {
        this.isUploadingCauzeImage = true;

        const token = await getToken();
        const url = UPLOAD_URL;
        const data = new FormData();
        data.append(CAUZE_IMAGE_FORM_KEY, imageFile);
        const res = await axios.put(url, data, {
          headers: {
            Authorization: token ? `Bearer ${token}` : null,
          },
        });

        this.updateCauze({
          imageId: res.data.id,
          previewImageUrl: res.data.urls.xl,
        });

        // This is because the upload put is so fast the user can't see
        // the progress spinner, made them think it didn't work sometimes
        setTimeout(() => {
          this.isUploadingCauzeImage = false;
        }, 2000);

        return {
          imageId: res.data.id,
          previewImageUrl: res.data.urls.xl,
        };
      } catch (err) {
        this.isUploadingCauzeImage = false;
        this.imageUploadError = true;
      }
    }
  };

  @action resetImage = () => {
    this.imageUploadError = false;
    this.updateCauze({ imageId: null, previewImageUrl: '' });
  };

  @action createPlannedEvent = async ({
    userContext,
    eventState = 'ACTIVE',
  }) => {
    this.savingCauze = true;
    const hasMatch =
      this.currentCauze.hasMatch &&
      this.currentCauze.userMatchLimit !== 0 &&
      this.currentCauze.matchTotal !== 0;

    const variables = {
      name: this.currentCauze.name.trim(),
      charityIds: this.currentCauze.charities.map((charity) => charity.id),
      description: this.currentCauze.description,
      previewUrl: this.currentCauze.previewUrl,
      externalImageUrl: this.currentCauze.externalImageUrl,
      imageID: this.currentCauze.imageId,
      hostContexts: userContext,
      eventState,
    };

    if (this.currentCauze.startDate && this.currentCauze.endDate) {
      variables.startDate = moment(this.currentCauze.startDate).format();
      variables.endDate = moment(this.currentCauze.endDate).format();
    }

    if (hasMatch) {
      variables.userMatchLimit =
        this.currentCauze.userMatchLimit * this.currentCauze.matchMultiplier;
      variables.matchTotal = this.currentCauze.matchTotal;
      variables.matchSponsorContexts = userContext;
      variables.matchType = this.currentCauze.matchType;
      variables.multiplier = this.currentCauze.matchMultiplier;
    }

    try {
      const res = await apolloClient.mutate({
        variables,
        mutation: createPlannedEventMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });

      if (this.videoData) {
        try {
          await api2Client.mutate({
            variables: {
              eventId: parseInt(res.data.createPlannedEvent.id),
              videoId: parseInt(this.videoData.id),
            },
            mutation: updateCauzeMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });

          const token = await getToken();
          const data = new FormData();
          data.append('file', this.videoData.video);

          await axios.post(
            `${UPLOAD_URLV2}/video/${this.videoData.id}/upload`,
            data,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : null,
              },
            },
          );
        } catch (err) {
          mixpanel.track('createPlannedEvent video upload error', {
            err: `${err}`,
          });
        }
      }

      if (res?.data?.createPlannedEvent?.id) {
        mixpanel.track('Cauze Created', {
          eventId: res?.data?.createPlannedEvent?.id,
        });
      }

      if (hasMatch) {
        profileStore.updateActiveEntityTotal(this.currentCauze.matchTotal * -1);
      }

      this.navToCauzes(userContext);
      this.onCreateUnmount();
      if (res.errors) {
        throw res.errors;
      }
    } catch (err) {
      mixpanel.track('createPlannedEvent Error', { err: `${err}` });
      err.map((err) =>
        uiStore.showNotification({
          body: err.message,
          type: 'ERROR',
        }),
      );
      this.createCauzeError = true;
      this.savingCauze = false;
    }

    this.savingCauze = false;
  };

  @action updatePlannedEvent = async ({
    eventState = 'ACTIVE',
    userContext,
  }) => {
    this.savingCauze = true;

    let startDate = this.currentCauze.startDate;
    let endDate = this.currentCauze.endDate;
    if (moment.isMoment(startDate) && !startDate.isValid()) startDate = null;
    if (moment.isMoment(endDate) && !endDate.isValid()) endDate = null;

    const variables = {
      name: this.currentCauze.name,
      charityIds: this.currentCauze.charities.map((charity) =>
        parseInt(charity.id),
      ),
      description: this.currentCauze.description,
      previewUrl: this.currentCauze.previewUrl,
      externalImageUrl: this.currentCauze.externalImageUrl,
      eventState,
      eventId: parseInt(this.currentCauze.id),
    };

    if (startDate && endDate) {
      variables.startDate = moment(startDate).format();
      variables.endDate = moment(endDate).format();
    } else {
      variables.startDate = null;
      variables.endDate = null;
    }

    // Only set the imageId for update if a new image has been uploaded, don't need to send if same
    if (this.currentCauze.imageId) {
      variables.imageId = this.currentCauze.imageId;
    }

    if (this.videoData) {
      try {
        variables.videoId = this.videoData.id;

        const token = await getToken();
        const data = new FormData();
        data.append('file', this.videoData.video);

        await axios.post(
          `${UPLOAD_URLV2}/video/${this.videoData.id}/upload`,
          data,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : null,
            },
          },
        );
      } catch (err) {
        delete variables.videoId;
      }
    }

    try {
      await api2Client.mutate({
        variables,
        mutation: updateCauzeMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });

      if (this.currentCauze.hasMatch) {
        if (this.currentCauze.hadPreviousMatch) {
          const variables = {
            id: this.currentCauze.matchId,
            matchLimit: this.currentCauze.userMatchLimit,
            matchTotal: this.currentCauze.matchTotal,
            matchType: this.currentCauze.matchType,
            multiplier: this.currentCauze.matchMultiplier,
          };

          const matchTotalDifference = this.getMatchTotalDifference();
          if (matchTotalDifference > 0) {
            variables.matchTotal = this.currentCauze.matchTotal;
          }

          await apolloClient.mutate({
            variables,
            mutation: editMatchMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });

          if (matchTotalDifference > 0) {
            profileStore.updateActiveEntityTotal(matchTotalDifference * -1);
          }
        } else {
          await apolloClient.mutate({
            variables: {
              userContext,
              eventId: this.currentCauze.id,
              matchLimit: this.currentCauze.userMatchLimit,
              matchTotal: this.currentCauze.matchTotal,
              matchType: this.currentCauze.matchType,
              multiplier: this.currentCauze.matchMultiplier,
            },
            mutation: createMatchMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });
          profileStore.updateActiveEntityTotal(
            this.currentCauze.matchTotal * -1,
          );
        }
      }

      this.navToCauzes(userContext);
    } catch (err) {
      this.createCauzeError = true;
      this.savingCauze = false;
      mixpanel.track('updatePlannedEvent Error', { err: `${err}` });
      err.map((err) =>
        uiStore.showNotification({
          body: err.message,
          type: 'ERROR',
        }),
      );
    }

    this.savingCauze = false;
  };

  @action loadCauzeToEdit = async ({ userContext, eventId }) => {
    this.loadingCauze = true;

    let options = {
      variables: {
        eventId: parseInt(eventId),
      },
      query: eventQueryApi2,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    const result = await api2Client.query(options);

    options.query = eventQuery;
    options.variables = {
      id: eventId,
      userContext,
    };

    const result2 = await apolloClient.query(options);
    const eventMatch = result2.data.event?.matchSummaries?.[0];

    let cauzeToEdit = result.data.event;

    if (!cauzeToEdit) {
      this.loadingCauzeError = true;
    } else {
      let matchToEdit = {};

      if (eventMatch && !userContext.charityId) {
        matchToEdit = eventMatch;
        matchToEdit.userMatchLimit = matchToEdit.matchLimit;
        matchToEdit.matchId = matchToEdit.id;
        matchToEdit.matchMultiplier = matchToEdit.multiplier;

        const sponsorMatch = matchToEdit.matchSponsors.filter(
          (matchSponsor) =>
            matchSponsor.id === userContext.companyId ||
            matchSponsor.id === userContext.userId,
        )[0];

        matchToEdit.matchTotal =
          sponsorMatch?.matchTotal || matchToEdit.matchTotal;

        delete matchToEdit['matchLimit'];
        delete matchToEdit['id'];
      }

      let cauzeDetails = {
        eventState: cauzeToEdit.eventState,
        name: cauzeToEdit.name,
        charities: cauzeToEdit.charities.map((charity) => ({
          ...charity,
          label: charity.name, // for the async select charity search component defaults
          key: charity.name, // ||
          location: `${charity.city}, ${charity.state}`,
          value: charity.id,
        })),
        previewUrl: cauzeToEdit.previewUrl,
        description: cauzeToEdit.description,
        startDate: cauzeToEdit.startDate
          ? moment(cauzeToEdit.startDate).utc()
          : '',
        endDate: cauzeToEdit.endDate ? moment(cauzeToEdit.endDate).utc() : '',
        previewImageUrl:
          cauzeToEdit.image && cauzeToEdit.image.lg ? cauzeToEdit.image.lg : '',
        id: eventId,
      };

      if (!isEmpty(matchToEdit)) {
        cauzeDetails = {
          ...matchToEdit,
          ...cauzeDetails,
          hadPreviousMatch: true,
          previousMatchTotal: matchToEdit.matchTotal,
          hasMatch: true,
        };
      }

      this.updateCauze(cauzeDetails);

      this.currentCauze.hasDates =
        !!cauzeToEdit.startDate || cauzeToEdit.endDate;
      this.loadingCauze = false;
    }
  };

  getMatchTotalDifference = () => {
    if (!this.currentCauze.previousMatchTotal) {
      return this.currentCauze.matchTotal;
    }

    return this.currentCauze.matchTotal - this.currentCauze.previousMatchTotal;
  };

  @action setMatchHasChanged = (matchHasChanged) => {
    this.matchHasChanged = matchHasChanged;
  };

  @action startVideoUpload = async ({ width, height, video }) => {
    const options = {
      variables: { width, height },
      mutation: createVideoMutation,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await api2Client.mutate(options);

      this.videoData = {
        width,
        height,
        video,
        id: result.data.createVideo.id,
      };
    } catch (err) {
      mixpanel.track('startVideoUploadError', { err: `${err}` });
      this.videoData = null;
    }
  };

  @action onUnmount = () => {
    this.isError = false;
    this.cauzes = [];
    this.cauzesJoinTotal = 0;
    this.cauzesAmountTotal = 0;
    this.cauzesLoading = false;
    this.isUploadingCauzeImage = false;
    this.imageUploadError = false;
    this.menuActiveForCharityId = null;
    this.videoData = null;
  };

  @action setLoading = (loading) => {
    this.isUploadingCauzeImage = loading;
  };

  @action clearCauzeErrors = () => {
    this.loadingCauzeError = false;
    this.createCauzeError = false;
  };

  @action onCreateUnmount = () => {
    this.currentCauze = {
      name: '',
      charities: [],
      eventState: 'UNPUBLISHED',
      description: '',
      startDate: '',
      endDate: '',
      imageId: '',
      previewImageUrl: '',
      hasDates: false,
      userMatchLimit: 5000,
      matchTotal: 0,
      hasMatch: false,
      hadPreviousMatch: false,
      previousMatchTotal: null,
      previewUrl: '',
      matchType: 'EVENT',
      matchMultiplier: 1,
      matchId: '',
    };
    this.matchHasChanged = false;
    this.createCauzeError = false;
    this.loadingCauze = false;
    this.loadingCauzeError = false;
    this.videoData = null;
  };

  @action deleteCauzeMatch = async ({ eventId }) => {
    await api2Client.mutate({
      variables: { eventId, delete: true },
      mutation: updateCauzeMatchMutation,
    });
  };

  constructor() {
    makeObservable(this);
  }
}

const cauzeStore = new CauzeStore();
export default cauzeStore;
